import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Mail, Send, ShieldAlert } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { errorFrom } from "@/lib/errors";
import { emailAdminMe, emailLogs, emailPreview, emailRecipientsCount, emailSend, type EmailAdminMe, type EmailDraft, type EmailLog, type EmailPreview, type EmailSendResult } from "@/lib/emailApi";
import { EmailComposeForm } from "@/components/admin/EmailComposeForm";
import { EmailPreviewPanel } from "@/components/admin/EmailPreviewPanel";
import { EmailLogsTable } from "@/components/admin/EmailLogsTable";
import { OfficialEmails } from "@/components/admin/OfficialEmails";
import type { EmailTemplateInfo } from "@/lib/emailApi";

type Step = "compose" | "preview" | "done";

const EMPTY: EmailDraft = { category: "SERVICE", target_type: "single", template_code: null, to: "", subject: "", title: "", message: "", cta_label: "", cta_url: "" };

// Hidden admin tool (not in the navbar): only MAIL_ADMIN_EMAIL passes the backend gate. Flow: compose -> preview -> confirm -> send.
export default function AdminEmail() {
  const { t } = useTranslation();
  const [me, setMe] = useState<EmailAdminMe | null>(null);
  const [denied, setDenied] = useState(false);
  const [counts, setCounts] = useState<{ SERVICE: number; MARKETING: number } | null>(null);
  const [draft, setDraft] = useState<EmailDraft>(EMPTY);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [result, setResult] = useState<EmailSendResult | null>(null);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>("compose");

  const refreshLogs = () => emailLogs().then(setLogs).catch(() => undefined);

  useEffect(() => {
    emailAdminMe()
      .then((m) => { setMe(m); emailRecipientsCount().then(setCounts).catch(() => undefined); refreshLogs(); })
      .catch(() => setDenied(true));
  }, []);

  const doPreview = async () => {
    setBusy(true);
    try {
      setPreview(await emailPreview(draft));
      setStep("preview");
    } catch (err) {
      toast.error(errorFrom(err));
    } finally {
      setBusy(false);
    }
  };

  const doSend = async () => {
    if (!preview || busy) return;
    setBusy(true);
    try {
      const r = await emailSend(preview.request_id);
      setResult(r);
      setStep("done");
      refreshLogs();
      (r.status === "FAILED" ? toast.error : toast.success)(t("adminEmail.resultToast", { sent: r.sent_count, failed: r.failed_count }));
    } catch (err) {
      toast.error(errorFrom(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setDraft(EMPTY); setPreview(null); setResult(null); setStep("compose"); };
  const sendNow = (tpl: EmailTemplateInfo) => {
    setDraft({ ...EMPTY, category: tpl.category, template_code: tpl.code, subject: tpl.subject });
    setPreview(null); setResult(null); setStep("compose");
    document.getElementById("admin-email-composer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (denied) {
    return (
      <Layout>
        <main className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="admin-email-denied">
          <ShieldAlert className="mx-auto h-10 w-10 text-red-400" />
          <h1 className="mt-4 text-2xl font-bold text-white">{t("adminEmail.deniedTitle")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("adminEmail.deniedBody")}</p>
        </main>
      </Layout>
    );
  }
  if (!me) {
    return <Layout><main className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-cyan-300" data-testid="admin-email-loading" /></main></Layout>;
  }
  const configured = me.config.resend_api_key && me.config.email_from;
  return (
    <Layout>
      <main className="mx-auto max-w-6xl px-6 py-12 sm:px-10" data-testid="admin-email-page">
        <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-cyan-300">{t("adminEmail.eyebrow")}</p>
        <h1 className="mt-2 flex items-center gap-3 text-3xl font-bold text-white sm:text-4xl"><Mail className="h-7 w-7 text-cyan-300" />{t("adminEmail.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("adminEmail.subtitle", { email: me.email, provider: me.provider })}</p>
        {!configured && (
          <div className="mt-4 rounded-xl border border-yellow-400/40 bg-yellow-400/10 px-4 py-3 text-sm text-yellow-200" data-testid="admin-email-not-configured">
            {t("adminEmail.notConfigured")}
          </div>
        )}
        <div className="mt-8"><OfficialEmails onSendNow={sendNow} /></div>
        <div className="mt-8 grid gap-8 lg:grid-cols-[1.1fr_1fr]">
          <section className="glass-panel min-w-0 rounded-2xl p-6" id="admin-email-composer">
            {step === "compose" && <EmailComposeForm draft={draft} counts={counts} busy={busy} onChange={setDraft} onPreview={doPreview} />}
            {step === "preview" && preview && <EmailPreviewPanel preview={preview} busy={busy} onBack={() => setStep("compose")} onConfirm={doSend} />}
            {step === "done" && result && (
              <div data-testid="admin-email-result" className="space-y-4">
                <h2 className="text-lg font-bold text-white">{t("adminEmail.resultTitle")}</h2>
                <dl className="grid grid-cols-3 gap-3 text-center">
                  {(["recipient_count", "sent_count", "failed_count"] as const).map((k) => (
                    <div key={k} className="rounded-xl border border-white/10 bg-black/30 p-3">
                      <dt className="text-[11px] uppercase tracking-widest text-muted-foreground">{t(`adminEmail.${k}`)}</dt>
                      <dd className="mt-1 text-2xl font-bold text-white" data-testid={`admin-email-result-${k}`}>{result[k]}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-sm text-muted-foreground" data-testid="admin-email-result-status">{t("adminEmail.status")}: <span className="font-semibold text-white">{result.status}</span></p>
                <button type="button" onClick={reset} data-testid="admin-email-new-btn" className="cta-hero inline-flex items-center gap-2 px-5 py-2 text-sm"><Send className="h-4 w-4" />{t("adminEmail.newEmail")}</button>
              </div>
            )}
          </section>
          <section className="glass-panel min-w-0 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-white">{t("adminEmail.logsTitle")}</h2>
            <EmailLogsTable logs={logs} />
          </section>
        </div>
      </main>
    </Layout>
  );
}
